/**
 * Preview-mode rows for the notification feed.
 *
 * One per type, so Lovable preview exercises every icon, every copy key and
 * every CTA branch — including the ones whose producers only fire in
 * production. Never delete these: preview is how the UI is developed, and the
 * dual-mode contract depends on them (docs/patterns/00-lovable-preview-mode.md).
 *
 * Deliberately contains NO `t()` call. These are built at module scope, and a
 * `t()` evaluated at import freezes the language the app started in
 * (CLAUDE.md). Copy comes from the type's i18n keys at render time; these rows
 * carry only `params` and a `fallbackTitle` for the unknown-type path.
 *
 * Timestamps are relative to load, not hardcoded, so the relative-time
 * rendering stays meaningful instead of drifting to "2 years ago".
 */

import type { FeedNotification } from "@/lib/notifications/types";

const minutesAgo = (m: number) => new Date(Date.now() - m * 60_000).toISOString();

export const dummyNotifications: FeedNotification[] = [
  {
    id: "n1",
    type: "followUpDrafted",
    createdAt: minutesAgo(35),
    readAt: null,
    params: { meetingTitle: "HubSpot Discovery Call" },
    target: { kind: "meeting", meetingId: "p3", panel: "follow_up" },
    fallbackTitle: "Follow-up email drafted",
    fallbackBody: null,
  },
  {
    id: "n2",
    type: "taskSuggestion",
    createdAt: minutesAgo(95),
    readAt: null,
    params: { count: 3, meetingTitle: "Datadog Architecture Review" },
    target: { kind: "meeting", meetingId: "p3", panel: "tasks" },
    fallbackTitle: "Tasks suggested",
    fallbackBody: null,
  },
  {
    id: "n3",
    type: "dealSuggestion",
    createdAt: minutesAgo(140),
    readAt: null,
    // Both ids are required by the target parser; the CTA is meeting-scoped.
    target: { kind: "dealSuggestion", suggestionId: "ds1", meetingId: "p3" },
    params: { count: 1, meetingTitle: "Weekly Sales Sync" },
    fallbackTitle: "Deal suggested",
    fallbackBody: null,
  },
  {
    id: "n4",
    type: "weekdayMorning",
    createdAt: minutesAgo(300),
    readAt: minutesAgo(280),
    params: { count: 4 },
    target: { kind: "today" },
    fallbackTitle: "Your morning brief",
    fallbackBody: null,
  },
  {
    id: "n5",
    type: "weekdayEndOfDay",
    createdAt: minutesAgo(1_500),
    readAt: minutesAgo(1_480),
    params: { count: 2 },
    target: { kind: "today" },
    fallbackTitle: "Your day in review",
    fallbackBody: null,
  },
  {
    id: "n6",
    type: "weekStart",
    createdAt: minutesAgo(2_900),
    readAt: minutesAgo(2_880),
    params: { count: 11 },
    target: { kind: "today" },
    fallbackTitle: "Your week ahead",
    fallbackBody: null,
  },
  {
    id: "n7",
    type: "firstMeeting",
    createdAt: minutesAgo(4_300),
    readAt: minutesAgo(4_200),
    params: {},
    target: { kind: "meeting", meetingId: "p3", panel: "summary" },
    fallbackTitle: "Your first meeting is in",
    fallbackBody: null,
  },
  {
    id: "n8",
    type: "calendarSyncCompleted",
    createdAt: minutesAgo(5_800),
    readAt: minutesAgo(5_700),
    params: {},
    target: { kind: "today" },
    fallbackTitle: "Calendar connected",
    fallbackBody: null,
  },
  // --- The alert list from the notifications spec. No producer emits these yet, so preview
  // is the only place they can be seen — which is the point: the icons, copy and CTA labels
  // are reviewable now rather than on the day the first one fires.
  {
    id: "n10",
    type: "assignedTask",
    createdAt: minutesAgo(12),
    readAt: null,
    params: { taskTitle: "Send proposal to Stripe", assignerName: "Sarah Chen" },
    target: { kind: "task", taskId: "1" },
    fallbackTitle: "Assigned task",
    fallbackBody: null,
  },
  {
    id: "n11",
    type: "assignedDealOwner",
    createdAt: minutesAgo(60),
    readAt: null,
    params: { dealName: "Shopify Annual Contract", assignerName: "Lisa Park" },
    target: { kind: "deal", dealId: "2" },
    fallbackTitle: "Assigned as deal owner",
    fallbackBody: null,
  },
  {
    id: "n12",
    type: "dailyBriefing",
    createdAt: minutesAgo(420),
    readAt: null,
    params: {},
    target: { kind: "today" },
    fallbackTitle: "Daily briefing",
    fallbackBody: null,
  },
  {
    id: "n13",
    type: "actionItemsDue",
    createdAt: minutesAgo(420),
    readAt: null,
    params: {},
    target: { kind: "today" },
    fallbackTitle: "Action items due",
    fallbackBody: null,
  },
  // The four Sunday checks, each its own row. They arrive together, which is exactly why
  // each one has a distinct icon — see presentation.ts.
  {
    id: "n14",
    type: "closeDateInPast",
    createdAt: minutesAgo(2_000),
    readAt: minutesAgo(1_900),
    params: {},
    target: { kind: "deals" },
    fallbackTitle: "Close date in the past",
    fallbackBody: null,
  },
  {
    id: "n15",
    type: "noActivity",
    createdAt: minutesAgo(2_000),
    readAt: minutesAgo(1_900),
    params: {},
    target: { kind: "deals" },
    fallbackTitle: "No activity in past 2 weeks",
    fallbackBody: null,
  },
  {
    id: "n16",
    type: "noNextSteps",
    createdAt: minutesAgo(2_000),
    readAt: minutesAgo(1_900),
    params: {},
    target: { kind: "deals" },
    fallbackTitle: "No next steps",
    fallbackBody: null,
  },
  {
    id: "n17",
    type: "fewContacts",
    createdAt: minutesAgo(2_000),
    readAt: minutesAgo(1_900),
    params: {},
    target: { kind: "deals" },
    fallbackTitle: "One or fewer contacts",
    fallbackBody: null,
  },
  {
    id: "n18",
    type: "weeklyPipelineChanges",
    createdAt: minutesAgo(2_000),
    readAt: minutesAgo(1_900),
    params: {},
    target: { kind: "deals" },
    fallbackTitle: "Weekly pipeline changes",
    fallbackBody: null,
  },
  {
    id: "n19",
    type: "crmSyncCompleted",
    createdAt: minutesAgo(6_000),
    readAt: minutesAgo(5_900),
    params: { crmName: "Salesforce" },
    target: { kind: "settings", section: "integrations" },
    fallbackTitle: "Salesforce sync complete",
    fallbackBody: null,
  },
  {
    // A type this build does not know, which the backend is allowed to send
    // because the two repos deploy independently. Kept in preview so the
    // fallback path is visible during UI work rather than discovered in prod.
    id: "n9",
    type: null,
    createdAt: minutesAgo(7_000),
    readAt: minutesAgo(6_900),
    params: {},
    target: { kind: "none" },
    fallbackTitle: "Quarterly review ready",
    fallbackBody: "Your Q3 pipeline review has been generated.",
  },
];
